import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

/* clickable 이 막아주는 건 click · mousedown · touchstart 뿐이다 — SDK 안에서
   CustomOverlay 는 딱 그 셋에만 preventMap 을 건다(InfoWindow 는 여덟 개를 건다).
   그래서 나머지는 오버레이를 뚫고 지도까지 내려간다: 겹친 핀 목록을 휠로 굴리면
   목록 대신 지도가 확대되고, 두 번 누르면 확대되고, 주소를 우클릭하면 지도가
   브라우저 메뉴를 지운다. 오버레이 위에서 지도가 반응할 일은 없으므로 여기서 막는다.

   stopPropagation 이 아니라 preventMap 인 이유: SDK 가 제 InfoWindow 에 쓰는 방법이
   그것이고, '이번 이벤트는 지도가 무시한다'는 플래그를 세웠다 다음 tick 에 스스로
   푸는 방식이라 지도가 어느 노드에서 듣든 통한다. 이벤트 자체는 그대로 흘러가므로
   목록 스크롤도, 바깥의 다른 리스너도 건드리지 않는다. */
const LEAKS = ["wheel", "dblclick", "contextmenu"];

/**
 * 카카오 CustomOverlay 한 개 = React 포털 한 개.
 * 오버레이 자체는 명령형 객체라 이펙트가 수명을 쥐고, 내용물은 React 가 그린다.
 * 이렇게 해야 마커가 리렌더돼도 DOM 노드가 살아남아 애니메이션이 끊기지 않는다.
 */
/* yAnchor 는 내용물의 어느 지점이 좌표에 놓이는지다. 기본값 1 은 아래쪽 끝 — 마커의
   꼬리 끝이 실제 좌표를 가리키게 하려고 그렇게 잡았다. 꼬리 없이 좌표를 한가운데로
   삼는 표식(내 위치의 점)은 0.5 를 넘긴다. */
export function Overlay({ map, lat, lng, zIndex = 1, clickable = false, yAnchor = 1, children }) {
  const node = useMemo(() => document.createElement("div"), []);
  const overlay = useRef(null);

  useEffect(() => {
    if (!map) return;
    const kakao = window.kakao;
    const ov = new kakao.maps.CustomOverlay({
      content: node,
      position: new kakao.maps.LatLng(lat, lng),
      yAnchor,
      xAnchor: 0.5,
      clickable,
      zIndex,
    });
    ov.setMap(map);
    overlay.current = ov;
    return () => { ov.setMap(null); overlay.current = null; };
    // 좌표·zIndex 는 아래 이펙트에서 갱신한다. 여기 넣으면 오버레이를 통째로 다시 만든다.
  }, [map, node, clickable, yAnchor]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map || !clickable) return;
    const prevent = () => window.kakao.maps.event.preventMap();
    LEAKS.forEach((type) => node.addEventListener(type, prevent));
    return () => LEAKS.forEach((type) => node.removeEventListener(type, prevent));
  }, [map, node, clickable]);

  useEffect(() => {
    const ov = overlay.current;
    if (!ov) return;
    ov.setPosition(new window.kakao.maps.LatLng(lat, lng));
    ov.setZIndex(zIndex);
  }, [lat, lng, zIndex]);

  return map ? createPortal(children, node) : null;
}

/**
 * 지도 위의 원 하나. 반지름이 **미터**라 확대·축소를 따라 정확히 커지고 작아진다 —
 * 그래서 DOM 이 아니라 SDK 의 도형을 쓴다. div 로 그리면 미터↔픽셀 환산을 우리가
 * 하게 되고, 줌이 바뀔 때마다 다시 계산해야 한다.
 *
 * 지금 쓰는 곳은 현위치의 오차 반경 하나뿐이다(MyLocation.jsx).
 */
export function Circle({ map, lat, lng, radius, color, zIndex = 0 }) {
  const circle = useRef(null);

  useEffect(() => {
    if (!map) return;
    const kakao = window.kakao;
    const c = new kakao.maps.Circle({
      center: new kakao.maps.LatLng(lat, lng),
      radius,
      strokeWeight: 1,
      strokeColor: color,
      strokeOpacity: 0.35,
      strokeStyle: "solid",
      fillColor: color,
      fillOpacity: 0.12,
      zIndex,
    });
    c.setMap(map);
    circle.current = c;
    return () => { c.setMap(null); circle.current = null; };
    // 좌표·반지름은 아래에서 갱신한다. 갱신마다 다시 만들면 원이 깜빡인다.
  }, [map, color, zIndex]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const c = circle.current;
    if (!c) return;
    c.setPosition(new window.kakao.maps.LatLng(lat, lng));
    c.setRadius(radius);
  }, [lat, lng, radius]);

  return null;
}
